import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import {
  Box,
  Chip,
  Paper,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';

import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG } from '../../config/api.config';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';

const formatTimestamp = (value) =>
  value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-';

const getBatchId = (batch = {}) =>
  batch?.batchId ?? batch?.batch_id ?? batch?.id ?? batch?.batchIdentifier ?? '';

const normalizeRequests = (batch) => {
  if (Array.isArray(batch?.batchRequests)) return batch.batchRequests;
  if (Array.isArray(batch?.requests)) return batch.requests;
  return [];
};

const getBatchRowId = (row) =>
  String(getBatchId(row) || row.id || JSON.stringify(row));

const renderStatusChip = (value) => {
  const label = value ?? '-';
  const normalized = String(value ?? '').trim().toLowerCase();

  const isSuccess = normalized === 'closed' || normalized === 'completed';
  const isPending = normalized === 'open' || normalized === 'pending' || normalized === 'in_progress' || normalized === 'in progress';
  const isError = normalized === 'failed' || normalized === 'error' || normalized === 'rejected';

  let color = '#64748b';
  let bgColor = '#f1f5f9';
  let Icon = HelpOutlineRoundedIcon;

  if (isSuccess) {
    color = '#059669';
    bgColor = '#ecfdf5';
    Icon = CheckCircleRoundedIcon;
  } else if (isPending) {
    color = '#2563eb';
    bgColor = '#eff6ff';
    Icon = ScheduleRoundedIcon;
  } else if (isError) {
    color = '#dc2626';
    bgColor = '#fef2f2';
    Icon = ErrorRoundedIcon;
  }

  return (
    <Chip
      label={label}
      size="small"
      icon={<Icon style={{ fontSize: '1rem', color: 'inherit' }} />}
      sx={{
        height: 24,
        borderRadius: '6px',
        fontWeight: 500,
        
        textTransform: 'uppercase',
        letterSpacing: '0.02em',
        backgroundColor: bgColor,
        color: color,
        border: '1px solid',
        borderColor: alpha(color, 0.2),
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
};

const SystemHistory = () => {
  const { loading, error, data, execute } = useAPICall();
  const [selectedBatchRowId, setSelectedBatchRowId] = useState('');
  const [pagination, setPagination] = useState({closed_req :{ pageIndex: 0, pageSize: 10 }, selected_closed_req : { pageIndex: 0, pageSize: 10 }});
  const batches = useMemo(() => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  }, [data]);

  const fetchHistory = useCallback(async () => {
    try {
      await execute(API_CONFIG.history.getSystemHistory, null, {
        errorMessage: 'Unable to load system history.',
        retries: 1,
      });
    } catch {
    }
  }, [execute]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    if (batches.length === 0) {
      setSelectedBatchRowId('');
      return;
    }
    setSelectedBatchRowId((current) => {
      const exists = batches.some((batch) => getBatchRowId(batch) === current);
      if (current && exists) return current;
      return getBatchRowId(batches[0]);
    });
  }, [batches]);

  const selectedBatch = useMemo(
    () => batches.find((batch) => getBatchRowId(batch) === selectedBatchRowId) ?? null,
    [batches, selectedBatchRowId]
  );

  const requests = useMemo(
    () => (selectedBatch ? normalizeRequests(selectedBatch) : []),
    [selectedBatch]
  );

  const columns = useMemo(
    () => [
      {
        accessorFn: (row) => getBatchId(row) || '-',
        id: 'batchId',
        header: 'Batch ID',
        size: 80,
      },
      {
        accessorKey: 'batchCreatedTime',
        header: 'Created Time',
        size: 140,
        Cell: ({ cell }) => formatTimestamp(cell.getValue()),
      },
      {
        accessorKey: 'batchLastUpdatedTime',
        header: 'Modified Time',
        size: 140,
        Cell: ({ cell }) => formatTimestamp(cell.getValue()),
      },
      {
        accessorKey: 'batchStatus',
        header: 'Status',
        size: 90,
        Cell: ({ cell }) => renderStatusChip(cell.getValue()),
      },
      {
        accessorFn: (row) => normalizeRequests(row).length,
        id: 'requestCount',
        header: 'Request Count',
        size: 110,
      },
      {
        accessorKey: 'batchClosedBy',
        header: 'Closed By',
        size: 140,
      },
    ],
    []
  );

  const requestsColumns = useMemo(
    () => [
      {
        accessorFn: (row) =>
          row.requestId ||
          row.request_id ||
          row.id ||
          row.request?.requestId ||
          row.request?.request_id ||
          '',
        id: 'requestId',
        header: 'Request ID',
        size: 120,
      },
      {
        accessorFn: (row) =>
          row.requestMdmId ?? row.request_mdm_id ?? row.request?.mdmId ?? '-',
        id: 'mdmId',
        header: 'MDM ID',
        size: 120,
      },
      {
        header: 'ID',
        accessorFn: (row) => row.requestIssueId ?? '-',
        id: 'id',
        size: 100,
      },
      {
        accessorFn: (row) =>
          formatTimestamp(
            row.requestCreatedTime ??
              row.request_created_time ??
              row.request?.createdTime ??
              row.createdTime ??
              ''
          ),
        id: 'createdTime',
        header: 'Created Time',
        size: 140,
      },
      {
        accessorFn: (row) =>
          formatTimestamp(
            row.requestLastUpdatedTime ??
              row.request_last_updated_time ??
              row.request?.lastUpdatedTime ??
              row.updatedTime ??
              ''
          ),
        id: 'updatedTime',
        header: 'Last Updated Time',
        size: 140,
      },
      {
        accessorFn: (row) =>
          row.requestIssueType ??
          row.request?.issue ??
          row.issue ??
          '-',
        id: 'issue',
        header: 'Issue',
        size: 220,
      },
      {
        accessorFn: (row) =>
          row.requestStatus ??
          row.status ??
          row.request?.status ??
          row.request?.requestStatus ??
          '-',
        id: 'status',
        header: 'Status',
        size: 120,
        Cell: ({ cell }) => renderStatusChip(cell.getValue()),
      },
    ],
    []
  );

  const softSearchSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      transition: 'all 0.2s',
      '& fieldset': { borderColor: '#e2e8f0' },
      '&:hover fieldset': { borderColor: '#cbd5e1' },
      '&.Mui-focused fieldset': { borderColor: '#1E88E5', borderWidth: '1.5px' },
      '&.Mui-focused': { backgroundColor: '#fff' }
    },
    '& .MuiOutlinedInput-input': { py: 1,  }
  };

  return (
    <Box sx={{ pb: 2 }}>
      {/* <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: '#fff',
          p: 1.5,
          mb: 1
        }}
      >
        <Typography sx={{ fontWeight: 600,  color: '#1e293b', mb: 0.25 }}>
          System History
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
          Audit trail and batch processing history for system events.
        </Typography>
      </Paper> */}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <GlobalMaterialTable
            columns={columns}
            data={batches}
            isLoading={loading}
            emptyMessage="No closed batches available"
              enablePagination
              enableBottomToolbar
              // getRowId={(row) => getBatchRowId(row)}
              initialState={{ density: 'compact' }}
              onRowClick={(_, row) => {
                setSelectedBatchRowId(row?.original?.batchId);
              }}
              selectedRowId={selectedBatchRowId}
              enableStickyHeader
              tableTitle="Closed Batches"
              tableCount={batches.length}
              muiSearchTextFieldProps={{
                size: 'small',
                placeholder: 'Filter batches...',
                sx: softSearchSx,
              }}
              page={pagination?.closed_req?.pageIndex + 1}
              pageSize={pagination?.closed_req?.pageSize}
              onPageChange={(p) =>
              setPagination(prev => ({
                  ...prev,
                  closed_req: {
                    ...prev.closed_req,
                    pageIndex: p - 1,
                  },
                }))
              }
              onPageSizeChange={(ps) =>
                setPagination(prev => ({
                  ...prev,
                  closed_req: {
                    ...prev.closed_req,
                    pageSize: ps,
                  },
                }))
              } 
              muiTableBodyRowProps={({ row }) => ({
                sx: {
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  ...(row?.original?.batchId === selectedBatchRowId
                    ? {
                        backgroundColor: '#f1f5f9 !important',
                        '&:hover': { backgroundColor: '#f1f5f9 !important' },
                      }
                    : {
                        '&:hover': { backgroundColor: '#f8fafc' }
                      }),
                },
              })}
              muiTableContainerProps={{
              }}
            />
        </Box>

        <Box>
            <GlobalMaterialTable
              columns={requestsColumns}
              data={requests}
              emptyMessage={selectedBatch ? 'No requests captured for this batch.' : 'Select a batch above to view detailed requests.'}
              enablePagination
              enableBottomToolbar
              page={pagination?.selected_closed_req?.pageIndex + 1}
              pageSize={pagination?.selected_closed_req?.pageSize}
              onPageChange={(p) =>
                  setPagination(prev => ({
                    ...prev,
                    selected_closed_req: {
                      ...prev.selected_closed_req,
                      pageIndex: p - 1,
                    },
                  }))
                }
                onPageSizeChange={(ps) =>
                  setPagination(prev => ({
                    ...prev,
                    selected_closed_req: {
                      ...prev.selected_closed_req,
                      pageSize: ps,
                    },
                  }))
                }
              enableStickyHeader
              tableTitle="Batch Requests"
              tableCount={requests.length}
              muiSearchTextFieldProps={{
                size: 'small',
                placeholder: 'Search within batch...',
                sx: softSearchSx,
              }}
              muiTableContainerProps={{
                sx: { maxHeight: 'calc(100vh - 420px)' },
              }}
            />
        </Box>
        </Box>
    </Box>
  );
};

export default SystemHistory;
